import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MakSvg } from "../../components/Maps/MakSvg";
import "./style.css";

const SVG_WIDTH = 842;
const SVG_HEIGHT = 1007;

const Maps = ({ language = "en" }) => {
  const [viewBox, setViewBox] = useState({
    x: 0,
    y: 0,
    width: SVG_WIDTH,
    height: SVG_HEIGHT,
  });

  const [currentLocation, setCurrentLocation] = useState("");
  const [destination, setDestination] = useState("");
  const [activeEdgeIds, setActiveEdgeIds] = useState([]);
  const [hasShownRoute, setHasShownRoute] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const title = language === "en" ? "Maps" : "मानचित्र";
  const homeLabel = language === "en" ? "Home" : "मुख्य पृष्ठ";

  const zoom = (factor) => {
    setViewBox((vb) => {
      const newWidth = vb.width * factor;
      const newHeight = vb.height * factor;
      const dx = (vb.width - newWidth) / 2;
      const dy = (vb.height - newHeight) / 2;

      return {
        x: vb.x + dx,
        y: vb.y + dy,
        width: newWidth,
        height: newHeight,
      };
    });
  };

  const zoomIn = () => zoom(0.8);
  const zoomOut = () => zoom(1.25);

  const resetView = () => {
    setViewBox({ x: 0, y: 0, width: SVG_WIDTH, height: SVG_HEIGHT });
    setCurrentLocation("");
    setDestination("");
    setActiveEdgeIds([]);
    setHasShownRoute(false);
  };

  const nodeOrder = [
    "entry",
    "block-a",
    "block-b",
    "block-bc",
    "block-c",
    "block-d",
    "block-e",
    "exit",
  ];

  const locations = [
    { id: "entry", labelEn: "Entry", labelHi: "प्रवेश" },
    { id: "block-a", labelEn: "Block A", labelHi: "ब्लॉक A" },
    { id: "block-b", labelEn: "Block B", labelHi: "ब्लॉक B" },
    { id: "block-bc", labelEn: "Block BC", labelHi: "ब्लॉक BC" },
    { id: "block-c", labelEn: "Block C", labelHi: "ब्लॉक C" },
    { id: "block-d", labelEn: "Block D", labelHi: "ब्लॉक D" },
    { id: "block-e", labelEn: "Block E", labelHi: "ब्लॉक E" },
    { id: "exit", labelEn: "Exit", labelHi: "निकास" },
  ];

  const edgeViewBoxes = {
    "entry->block-a": { x: 300, y: 880, width: 260, height: 200 },
    "block-a->block-b": { x: 160, y: 780, width: 360, height: 260 },
    "block-b->block-bc": { x: 60, y: 720, width: 360, height: 260 },
    "block-bc->block-c": { x: 180, y: 680, width: 360, height: 260 },
    "block-c->block-d": { x: 260, y: 380, width: 360, height: 300 },
    "block-d->block-e": { x: 360, y: 200, width: 360, height: 360 },
    "block-e->exit": { x: 520, y: 650, width: 280, height: 260 },
  };

  const handleCurrentChange = (value) => {
    setCurrentLocation(value);
    if (!value || !destination) return;

    const currentIdx = nodeOrder.indexOf(value);
    const destIdx = nodeOrder.indexOf(destination);

    if (currentIdx !== -1 && destIdx !== -1 && destIdx <= currentIdx) {
      const nextNode = nodeOrder[currentIdx + 1] || value;
      setDestination(nextNode);
    }
  };

  const handleDestinationChange = (value) => {
    setDestination(value);
  };

  const handleShowRoute = () => {
    if (!currentLocation || !destination) return;
    if (currentLocation === destination) return;

    const fromIdx = nodeOrder.indexOf(currentLocation);
    const toIdx = nodeOrder.indexOf(destination);
    if (fromIdx === -1 || toIdx === -1) return;
    if (toIdx <= fromIdx) return;

    const edges = [];
    for (let i = fromIdx; i < toIdx; i++) {
      const from = nodeOrder[i];
      const to = nodeOrder[i + 1];
      edges.push(`${from}->${to}`);
    }
    if (!edges.length) return;

    setActiveEdgeIds(edges);

    const fullRouteVb = getRouteViewBox(edges, edgeViewBoxes, {
      x: 0,
      y: 0,
      width: SVG_WIDTH,
      height: SVG_HEIGHT,
    });
    setViewBox(fullRouteVb);

    setHasShownRoute(true);
    setShowModal(true);
  };

  const destinationOptions = locations.filter((loc) => {
    if (loc.id === currentLocation) return false;
    const fromIdx = nodeOrder.indexOf(currentLocation);
    const destIdx = nodeOrder.indexOf(loc.id);
    return destIdx > fromIdx;
  });

  const getRouteViewBox = (edgeKeys, edgeViewBoxes, fallback) => {
    const boxes = edgeKeys.map((key) => edgeViewBoxes[key]).filter(Boolean);

    if (!boxes.length) return fallback;

    let minX = boxes[0].x;
    let minY = boxes[0].y;
    let maxX = boxes[0].x + boxes[0].width;
    let maxY = boxes[0].y + boxes[0].height;

    boxes.forEach((b) => {
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.width);
      maxY = Math.max(maxY, b.y + b.height);
    });

    let width = maxX - minX;
    let height = maxY - minY;

    const padding = 40;
    minX -= padding;
    minY -= padding;
    width += padding * 2;
    height += padding * 2;

    const extraFactor = 1.3; 
    const centerX = minX + width / 2;
    const centerY = minY + height / 2;
    const finalWidth = width * extraFactor;
    const finalHeight = height * extraFactor;

    return {
      x: centerX - finalWidth / 2,
      y: centerY - finalHeight / 2,
      width: finalWidth,
      height: finalHeight,
    };
  };
  return (
    <div>
      <div
        className="inner-pages-head"
        style={{ backgroundImage: `url('images/about-header-bg.jpg')` }}
      >
        <div className="container">
          <div className="title_and_breadcrumbs">
            <h1>{title}</h1>
            <ul>
              <li>
                <Link to="/">{homeLabel}</Link>
              </li>
              <span></span>
              <li>{title}</li>
            </ul>
          </div>
          <div className="banner-logo">
            <img src="images/mahabharat-logo.svg" alt="" />
          </div>
        </div>
      </div>

      <section className="mt-5 mb-5 p-3">
        <div className="search_location_map">
          <div className="container">
            <div className="row">
              <div className="col-md-12 mb-2">
                <label className="form-label">
                  {language === "en" ? "Current location" : "वर्तमान स्थान"}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={currentLocation}
                  onChange={(e) => handleCurrentChange(e.target.value)}
                >
                  <option value="">
                    {language === "en"
                      ? "Select current location"
                      : "वर्तमान स्थान चुनें"}
                  </option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {language === "en" ? loc.labelEn : loc.labelHi}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-12 mb-2">
                <label className="form-label">
                  {language === "en" ? "Destination" : "गंतव्य"}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={destination}
                  onChange={(e) => handleDestinationChange(e.target.value)}
                >
                  <option value="">
                    {language === "en" ? "Select destination" : "गंतव्य चुनें"}
                  </option>
                  {destinationOptions.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {language === "en" ? loc.labelEn : loc.labelHi}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-12 d-flex align-items-end mb-2">
                <button
                  className="btn btn-sm btn-success me-2"
                  onClick={handleShowRoute}
                >
                  {language === "en" ? "Show route" : "मार्ग दिखाएँ"}
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={resetView}
                >
                  {language === "en" ? "Reset" : "रीसेट"}
                </button>
              </div>
            </div>
            {/* {hasShownRoute ? (
              <>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <button className="btn btn-sm btn-primary" onClick={zoomIn}>
                    +
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={zoomOut}>
                    -
                  </button>
                </div>
                <div
                  style={{
                    width: "100%",
                    maxWidth: "900px",
                    height: "600px",
                    border: "1px solid #ccc",
                    overflow: "auto",
                  }}
                >
                  <MakSvg
                    viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
                    activeEdgeIds={activeEdgeIds}
                    style={{ width: "100%", height: "100%", display: "block" }}
                  />
                </div>
              </>
            ) : null} */}
            <div
              className={`modal fade ${showModal ? "show d-block" : ""}`}
              tabIndex="-1"
              role="dialog"
              style={{
                backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
              }}
              onClick={() => setShowModal(false)}
            >
              <div
                className="modal-dialog modal-xl modal-dialog-centered"
                role="document"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {language === "en" ? "Route map" : "मार्ग मानचित्र"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>

                  <div className="modal-body">
                    {hasShownRoute ? (
                      <>
                        <div className="d-flex flex-wrap gap-2 mb-2 zoom-btns">
                          <button
                            className="btn btn-sm btn-primary"
                            type="button"
                            onClick={zoomIn}
                          >
                            +
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            type="button"
                            onClick={zoomOut}
                          >
                            -
                          </button>
                        </div>

                        <div
                          style={{
                            width: "100%",
                            height: "600px",
                            border: "1px solid #ccc",
                            overflow: "auto",
                          }}
                        >
                          <MakSvg
                            viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
                            activeEdgeIds={activeEdgeIds}
                            style={{
                              width: "900px",
                              height: "1000px",
                              display: "block",
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <p>
                        {language === "en"
                          ? "Select current location and destination, then click Show route."
                          : "कृपया वर्तमान स्थान और गंतव्य चुनें, फिर मार्ग दिखाएँ पर क्लिक करें।"}
                      </p>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      {language === "en" ? "Close" : "बंद करें"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Maps;
